import type { WhatsappTemplatesTranslations } from '../interfaces/whatsappTemplates';

export const whatsappTemplatesTranslations: WhatsappTemplatesTranslations = {
  page_title: 'WhatsApp templates',
  page_subtitle:
    'Messages pre-approved by Meta. They are the only ones you can send outside the 24-hour window.',
  new_template: 'New template',
  sync_from_meta: 'Sync from Meta',
  sync_success: 'Synced {{count}} templates from Meta',
  sync_empty: 'No new templates in Meta',
  search_placeholder: 'Search by name…',

  column_name: 'Name',
  column_category: 'Category',
  column_language: 'Language',
  column_status: 'Status',
  column_quality: 'Quality',
  column_updated: 'Updated',
  column_actions: 'Actions',

  filter_all: 'All',
  filter_approved: 'Approved',
  filter_pending: 'In review',
  filter_rejected: 'Rejected',

  status_APPROVED: 'Approved',
  status_IN_APPEAL: 'In appeal',
  status_PENDING: 'In review',
  status_PENDING_DELETION: 'Deleting',
  status_DELETED: 'Deleted',
  status_DISABLED: 'Disabled',
  status_LIMIT_EXCEEDED: 'Limit exceeded',
  status_REJECTED: 'Rejected',
  status_PAUSED: 'Paused',

  category_MARKETING: 'Marketing',
  category_UTILITY: 'Utility',
  category_AUTHENTICATION: 'Authentication',
  category_MARKETING_hint:
    'Promotions, announcements and cart recovery. Requires the customer to have opted in.',
  category_UTILITY_hint:
    'Order confirmations, shipping updates, appointment reminders. The easiest category to get approved.',
  category_AUTHENTICATION_hint:
    'One-time codes. Meta enforces a fixed shape we do not support yet.',

  empty_title: 'No templates yet',
  empty_description:
    'Create your first template, or pull in the ones that already exist in your Meta account.',
  empty_action: 'Create template',
  not_connected_title: 'Connect WhatsApp first',
  not_connected_description:
    'Templates live in your WhatsApp Business account. Connect it to view and create them here.',
  not_connected_action: 'Go to integrations',
  no_business_id_title: 'Missing details from your Meta account',
  no_business_id_description:
    'Your account was connected before we stored the business portfolio id, which Meta requires to manage templates. Reconnect WhatsApp to fill it in.',
  no_business_id_action: 'Reconnect',
  rejected_reason_label: 'Rejection reason',

  action_edit: 'Edit',
  action_send: 'Send',
  action_duplicate: 'Duplicate',
  action_delete: 'Delete',

  delete_title: 'Delete template',
  delete_description:
    'Delete “{{name}}”? This cannot be undone.',
  delete_all_languages_warning:
    'Meta deletes by name: every language version of this template will be removed.',
  delete_confirm: 'Delete',
  delete_cancel: 'Cancel',
  delete_success: 'Template deleted',

  editor_create_title: 'New template',
  editor_edit_title: 'Edit template',
  editor_save: 'Save and submit to Meta',
  editor_cancel: 'Cancel',
  editor_create_success: 'Template submitted to Meta for review',
  editor_update_success: 'Template updated and resubmitted for review',
  editor_readonly_notice: 'This template cannot be edited in its current state.',

  section_identity: 'Identity',
  section_identity_hint:
    'Meta freezes these fields on creation; only the content can change afterwards.',
  field_name: 'Name',
  field_name_hint: 'Lowercase letters, numbers and underscores only.',
  field_name_preview: 'Will be created as:',
  field_category: 'Category',
  field_language: 'Language',
  field_parameter_format: 'Variable format',
  format_positional: 'Numbered · {{sample}}',
  format_named: 'Named · {{sample}}',
  format_hint: 'Cannot be changed after the template is created.',
  identity_locked_notice:
    'Meta does not allow changing the name, category or language of an existing template.',

  section_header: 'Header',
  section_header_hint: 'Optional. A short bold line above the message.',
  header_kind_NONE: 'No header',
  header_kind_TEXT: 'Text',
  header_kind_IMAGE: 'Image',
  header_kind_VIDEO: 'Video',
  header_kind_DOCUMENT: 'Document',
  header_kind_LOCATION: 'Location',
  header_media_unavailable:
    'Media headers cannot be created from {{brand}} yet.',
  header_preserved_notice:
    'This template uses a header that cannot be edited here. It will be kept as-is when you save.',
  field_header_text: 'Header text',

  section_body: 'Message',
  section_body_hint:
    'The body of the message. Use variables to personalize it per customer.',
  field_body_text: 'Message text',
  add_variable: 'Add variable',
  renumber_variables: 'Renumber variables',
  renumber_hint:
    'Numbered variables must run from 1 with no gaps. Meta rejects the template if one is missing.',

  section_footer: 'Footer',
  section_footer_hint: 'Optional. Small grey text below the message.',
  footer_enable: 'Add a footer',
  field_footer_text: 'Footer text',

  section_buttons: 'Buttons',
  section_buttons_hint: 'Optional. Up to 3 buttons.',
  add_button: 'Add button',
  button_type_QUICK_REPLY: 'Quick reply',
  button_type_URL: 'Open link',
  button_type_PHONE_NUMBER: 'Call',
  field_button_text: 'Button text',
  field_button_url: 'Link',
  field_button_phone: 'Phone',
  button_move_up: 'Move up',
  button_move_down: 'Move down',
  button_remove: 'Remove button',

  section_variables: 'Variable examples',
  variables_required_notice:
    'Meta rejects templates whose variables have no example value. They are used for review only and are never sent to your customers.',
  variables_empty:
    'This message has no variables. Type {{sample}} in the text to add one.',
  field_example_value: 'Example value',
  variable_label: 'Variable',

  preview_title: 'Preview',
  preview_empty: 'Write the message to see the preview.',

  confirm_edit_title: 'Editing an approved template',
  confirm_edit_description:
    'Saving sends this template back to Meta for review, and it cannot be used for sends until it is approved again.',
  confirm_edit_accept: 'Understood, save',
  confirm_edit_cancel: 'Cancel',

  validation_name_required: 'Enter a name',
  validation_name_format: 'Lowercase letters, numbers and underscores only',
  validation_name_hex24:
    'This name collides with our internal id format. Add or remove a character.',
  validation_name_duplicate:
    'You already have a template with this name and language',
  validation_body_required: 'The message cannot be empty',
  validation_body_max: '1024 characters max',
  validation_footer_max: '60 characters max',
  validation_header_max: '60 characters max',
  validation_example_required: 'Meta requires an example for every variable',
  validation_positional_gap: 'Variables must run from 1 with no gaps',
  validation_named_format:
    'Named variables must start with a lowercase letter and use only letters, numbers and underscores',
  validation_edge_variable:
    'The message cannot start or end with a variable',
  validation_adjacent_variables: 'Two variables cannot be adjacent',
  validation_footer_no_variables: 'The footer cannot contain variables',
  validation_header_one_variable: 'The header allows at most one variable',
  validation_button_text_required: 'Enter the button text',
  validation_button_url: 'Enter a valid link',
  validation_button_phone: 'Enter a phone in international format',
  validation_buttons_max: '3 buttons max',
  validation_url_buttons_max: '2 link buttons max',
  validation_phone_buttons_max: '1 call button max',
  validation_wrong_format_tokens:
    'This template uses {{format}} variables. Use that format or Meta will not detect them.',

  send_title: 'Send template',
  send_description:
    'Sent from your WhatsApp Business number, straight into the customer’s chat.',
  send_template_label: 'Template',
  send_template_placeholder: 'Pick an approved template',
  send_from_label: 'Send from',
  send_to_label: 'Send to',
  send_to_placeholder: '+57 319 684 4292',
  send_to_invalid: 'Include the country code, e.g. +57 319 684 4292',
  send_fill_examples: 'Fill with examples',
  send_submit: 'Send',
  send_cancel: 'Cancel',
  send_success: 'Message sent',
  send_no_approved_title: 'No approved templates',
  send_no_approved_description:
    'Only templates Meta has already approved can be sent.',
  send_no_approved_action: 'View templates',
  send_media_link_label: 'File link',
  send_media_link_hint:
    'This template has media in its header. Paste a public link to the file.',
  send_variables_title: 'Variables',

  error_UNAUTHORIZED: 'Your session expired. Please sign in again.',
  error_FORBIDDEN:
    'Your role has no permissions over WhatsApp templates. Ask an administrator for them.',
  error_NOT_FOUND: 'Template not found',
  error_VALIDATION: 'Meta rejected the data: {{detail}}',
  error_NO_WABA_TOKEN:
    'Your organization has no WhatsApp connected yet. Connect it in Settings → Integrations.',
  error_TEMPLATE_EXISTS:
    'A template with that name and language already exists',
  error_META_REJECTED: 'Meta rejected the request: {{detail}}',
  error_UNKNOWN: 'Something went wrong: {{detail}}',
};
